import {
  DataEntryGatedValue,
  DetailRequirement,
  EngagementTaskTextGateParams,
  EngagementTaskTrait,
  MultipleTaskTraitDetail,
  SingularTaskTraitDetail,
} from '@cibo/core'
import { FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { useDebounceCallback } from '@react-hook/debounce'
import { ChangeEvent, useCallback, useState } from 'react'
import { useProgramEngagementContext } from '../../../../context'

type DataEntryGateGroupProps = {
  detailRequirement?: DetailRequirement
  onChange: (detail: MultipleTaskTraitDetail | SingularTaskTraitDetail) => void
  traitDetailValue: any
  immutable?: boolean
  traitConfig: EngagementTaskTrait
}

const detailIsValid = (value: DataEntryGatedValue | null) =>
  value &&
  (value.gate === false || (value.gate === true && !!value.data && value.data.length !== 0))

export const DataEntryGateGroup = ({
  detailRequirement,
  onChange,
  traitConfig,
  traitDetailValue,
  immutable,
}: DataEntryGateGroupProps) => {
  const { taskData } = useProgramEngagementContext()

  const traitStatus = taskData.data?.getTraitStatus(traitConfig.traitId)
  const params = traitStatus?.params

  const gate = (params as EngagementTaskTextGateParams)?.gate

  const [radioValue, setRadioValue] = useState(traitDetailValue?.gate)
  const [dataValue, setDataValue] = useState(traitDetailValue?.data || undefined)

  const handleChangeDetail = useDebounceCallback((detail: { value: DataEntryGatedValue }) => {
    const update = detailIsValid(detail.value as DataEntryGatedValue)
      ? detail
      : // yes, we're intentionally setting the value to 'null' if the value is invalid, so the user
        // doesn't see an active 'next' if they've clicked between true and false gate values
        // and not entered a 'data' value
        { ...detailForValue(detail.value?.gate, ''), value: null }

    onChange({ traitId: traitConfig.traitId, ...update })
  }, 300)

  const detailForValue = useCallback(
    (radioValue: boolean, dataValue: string) => {
      const value: DataEntryGatedValue = radioValue
        ? {
            gate: radioValue,
            data: dataValue,
          }
        : { gate: radioValue }
      if (detailRequirement?.year) {
        return {
          traitId: traitConfig.traitId,
          year: detailRequirement.year,
          value,
        }
      }
      return { traitId: traitConfig.traitId, value }
    },
    [traitConfig, detailRequirement]
  )

  const onChangeText = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>, radioValue: boolean) => {
      setRadioValue(true)
      const value = event.target.value
      setDataValue(value)

      const detail = detailForValue(radioValue, value)

      handleChangeDetail(detail)
    },
    [radioValue, detailForValue]
  )

  return (
    <RadioGroup
      value={radioValue}
      onChange={(_, radioValue) => {
        const option = radioValue === 'true' ? true : false

        setRadioValue(option)
        handleChangeDetail(detailForValue(option, dataValue))
      }}
    >
      <FormControlLabel
        key="false"
        disabled={immutable}
        value={false}
        control={<Radio />}
        label={gate.falseLabel}
      />
      <FormControlLabel
        key="true"
        disabled={immutable}
        value={true}
        control={<Radio />}
        label={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>{gate.label}</Typography>
            <TextField
              value={dataValue}
              placeholder={gate.promptText}
              onFocus={() => {
                setRadioValue(true)
              }}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                onChangeText(event, radioValue)
              }
            />
          </Stack>
        }
      />
    </RadioGroup>
  )
}
